import React, { createContext, useContext, useState, useEffect } from 'react'
import { apiClient } from '@/api/client'

export interface FeatureFlags {
  // Phase 5: Unified Marketing Command Center
  phase5_consolidated_dashboard: boolean
  phase5_onboarding_flow: boolean
  phase5_inline_audience_selector: boolean
  phase5_navigation_restructure: boolean

  // Other features
  [key: string]: boolean
}

interface FeatureFlagsContextType {
  flags: FeatureFlags
  loading: boolean
  error: string | null
  isFeatureEnabled: (feature: keyof FeatureFlags) => boolean
}

const defaultFlags: FeatureFlags = {
  phase5_consolidated_dashboard: false,
  phase5_onboarding_flow: false,
  phase5_inline_audience_selector: false,
  phase5_navigation_restructure: false
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined)

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(defaultFlags)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        setLoading(true)
        setError(null)

        // Get user ID from localStorage or auth
        const userId = localStorage.getItem('userId') || 'anonymous'

        // Fetch feature flags from backend
        const response = await apiClient.get(`/feature-flags?userId=${userId}`)

        if (response.data.success) {
          setFlags(prev => ({
            ...prev,
            ...response.data.data
          }))
        }
      } catch (err: any) {
        console.error('Failed to fetch feature flags:', err)
        // Fall back to defaults on error
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    // Fetch on mount
    fetchFeatureFlags()

    // Refetch every 5 minutes to check for flag updates
    const interval = setInterval(fetchFeatureFlags, 5 * 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
    return flags[feature] ?? false
  }

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, error, isFeatureEnabled }}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext)
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider')
  }
  return context
}

/**
 * Check if a feature is enabled
 * Usage: useFeature('phase5_consolidated_dashboard')
 */
export const useFeature = (feature: keyof FeatureFlags): boolean => {
  const { isFeatureEnabled } = useFeatureFlags()
  return isFeatureEnabled(feature)
}
